/**
 * The rows a collapsed chat card draws of a table its change touches: the header,
 * every row that holds one of the card's changes, and, while the table is short
 * enough, the unchanged rows right below a change. Where rows are left out, a gap
 * row (`TABLE_ROWS_EXCERPT_GAP`) stands in their place and says how many.
 *
 * WHY only rows below a change
 *   The collapsed card is cut at a fixed height. A row drawn above a change pushes
 *   the change down toward that cut: with "Service 05" kept above a row the card
 *   adds after it, the added row was half cut off. Rows below a
 *   change only ever sit under it.
 *
 * WHY rows, not the first lines of the table
 *   A collapsed card is cut at a fixed height. Drawn whole, a fifty-row table showed
 *   its header and the first few rows, and a change in row forty sat below the cut.
 *   Drawing the rows that change keeps the change in view; expanding the card draws
 *   the whole table again.
 *
 * WHY the excerpt's test for a change
 *   A row is kept when one of its paragraphs holds a change by the excerpt's own test
 *   (`ChangeExcerpt.paragraphHoldsChange`), so the collapsed card and the drawer's
 *   excerpt agree on which rows a card changes. Unlike the excerpt, the cells are
 *   drawn whole: a collapsed table still reads as the table.
 *
 * CASES, per table that no other table holds
 *   - it has no more rows than the budget       -> drawn whole
 *   - no row of it holds a change               -> drawn whole: there is nothing to keep in view
 *   - otherwise                                 -> its header rows and changed rows are kept, then
 *                                                  the unchanged rows right below a change, nearest
 *                                                  first, until the budget is met; each run of rows
 *                                                  left out becomes one gap row
 *
 * EXAMPLE, rowBudget 4, rows by index (0 is the header)
 *   3 rows, index 1 changed                  -> 0, 1, 2: the table fits the budget
 *   50 rows, indexes 12, 30 and 31 changed   -> 0, gap of 11 (1-11), 12, gap of 17 (13-29), 30, 31, gap of 18 (32-49)
 *   6 rows, index 2 changed                  -> 0, gap of 1 (1), 2, 3, 4, gap of 1 (5): 3 and 4 sit below the change
 *   30 rows, index 29 (the last) changed     -> 0, gap of 28 (1-28), 29: no row sits below it
 */
import { ChangeExcerpt, CHANGE_EXCERPT_NAMESPACE, type ExcerptChangeReading } from "./change-excerpt";

/** What a collapsed card keeps of each table: the card's own changes, and how many rows fit. */
export interface TableRowsExcerptOptions extends ExcerptChangeReading {
    /** How many rows a table is drawn with before rows are left out; the header and every changed row are kept past it. */
    rowBudget: number;
}

/** The element standing where a run of rows was left out; the renderer draws it as one short row. */
export const TABLE_ROWS_EXCERPT_GAP = "dx:rowGap";

/** The attribute of `TABLE_ROWS_EXCERPT_GAP` that holds how many rows were left out. */
export const TABLE_ROWS_EXCERPT_GAP_COUNT = "dx:count";

/** The attribute of `TABLE_ROWS_EXCERPT_GAP` that holds how many grid columns the table has, so the gap row spans it. */
export const TABLE_ROWS_EXCERPT_GAP_COLUMNS = "dx:columns";

/** `Node.ELEMENT_NODE`, named here because a server's XML host has no `Node` global. */
const ELEMENT_NODE_TYPE = 1;

/** One row of a table, where it stands among the table's own rows, and whether the card changes it. */
interface TableRowReading {
    row: Element;
    rowIndex: number;
    holdsChange: boolean;
}

/**
 * An unchanged row a collapsed table may draw: where it stands, and how many rows
 * below the nearest changed row above it.
 *
 * EXAMPLE
 *   changed row index 6, row index 8 -> { rowIndex: 8, rowsBelowChange: 2 }
 */
interface UnchangedRowBelowAChange {
    rowIndex: number;
    rowsBelowChange: number;
}

export class TableRowsExcerpt {
    /** Cuts every table the document part holds down to its collapsed rows, in place. */
    static cut(document: Document, options: TableRowsExcerptOptions): void {
        const root = ChangeExcerpt.documentPartRootOf(document);
        const outermostTables = Array.from(root.getElementsByTagName("w:tbl")).filter((table) => !TableRowsExcerpt.standsInTable(table, root));
        for (const table of outermostTables) TableRowsExcerpt.cutRowsOf(document, table, options);
    }

    /**
     * Cuts one table's rows.
     *
     * CASES
     *   - the table has no more rows than the budget -> nothing is left out
     *   - no row holds a change                      -> nothing is left out
     *   - otherwise                                  -> each run of rows not kept is replaced by one gap row
     */
    private static cutRowsOf(document: Document, table: Element, options: TableRowsExcerptOptions): void {
        const rowReadings = TableRowsExcerpt.ownRowsOf(table).map((row, rowIndex): TableRowReading => ({ row, rowIndex, holdsChange: TableRowsExcerpt.rowHoldsChange(row, options) }));
        /** WHEN the whole table fits the budget CONSEQUENCE it is drawn whole: leaving a row out saves nothing. */
        if (rowReadings.length <= options.rowBudget) return;
        const changedRowIndexes = rowReadings.filter((rowReading) => rowReading.holdsChange).map((rowReading) => rowReading.rowIndex);
        /** WHEN no row holds a change CONSEQUENCE there is no row to keep in view, so the table is drawn whole. */
        if (changedRowIndexes.length === 0) return;
        const keptRowIndexes = TableRowsExcerpt.keptRowIndexesOf(rowReadings, changedRowIndexes, options.rowBudget);
        const columnCount = TableRowsExcerpt.gridColumnCountOf(table);
        let rowsLeftOutInThisRun: TableRowReading[] = [];
        for (const rowReading of rowReadings) {
            /** WHEN the row is left out CONSEQUENCE it joins the run that one gap row will stand for. */
            if (!keptRowIndexes.has(rowReading.rowIndex)) {
                rowsLeftOutInThisRun.push(rowReading);
                continue;
            }
            TableRowsExcerpt.replaceRunWithGap(document, rowsLeftOutInThisRun, columnCount);
            rowsLeftOutInThisRun = [];
        }
        /** EDGE CASE: rows left out after the last kept row end the table with a gap row of their own. */
        TableRowsExcerpt.replaceRunWithGap(document, rowsLeftOutInThisRun, columnCount);
    }

    /**
     * The indexes of the rows drawn: the header rows, every changed row, and then the
     * unchanged rows right below a change, one at a time, until `rowBudget` rows are kept.
     *
     * EXAMPLE, rowBudget 4, 10 rows, row index 5 changed (the sixth row)
     *   kept first   {0, 5}                 the header and the changed row
     *   below it     6 (1 below), 7 (2)     -> {0, 5, 6}, then {0, 5, 6, 7}: 4 rows, the budget is met
     */
    private static keptRowIndexesOf(rowReadings: TableRowReading[], changedRowIndexes: number[], rowBudget: number): Set<number> {
        const keptRowIndexes = new Set<number>([...TableRowsExcerpt.headerRowIndexesOf(rowReadings), ...changedRowIndexes]);
        for (const unchangedRowIndex of TableRowsExcerpt.unchangedRowsBelowAChangeNearestFirst(rowReadings, keptRowIndexes, changedRowIndexes)) {
            /** WHEN the budget is met CONSEQUENCE no further unchanged row is drawn. */
            if (keptRowIndexes.size >= rowBudget) break;
            keptRowIndexes.add(unchangedRowIndex);
        }
        return keptRowIndexes;
    }

    /**
     * The table's header rows: the rows at its top Word repeats as a header
     * (`w:trPr/w:tblHeader`), or its first row when none is marked.
     *
     * EXAMPLE
     *   rows 0 and 1 carry w:tblHeader, row 2 does not -> [0, 1]
     *   no row carries it                              -> [0]
     */
    private static headerRowIndexesOf(rowReadings: TableRowReading[]): number[] {
        const markedHeaderRowCount = rowReadings.findIndex((rowReading) => !TableRowsExcerpt.isMarkedHeaderRow(rowReading.row));
        /**
         * CASES
         *   - the first row is no marked header (findIndex 0)       -> the first row alone is the header
         *   - every row is a marked header (findIndex -1)           -> every row: the table is all header
         *   - n rows at the top are marked (findIndex n)            -> rows 0 … n - 1
         */
        if (markedHeaderRowCount === 0) return [0];
        if (markedHeaderRowCount === -1) return rowReadings.map((rowReading) => rowReading.rowIndex);
        return rowReadings.slice(0, markedHeaderRowCount).map((rowReading) => rowReading.rowIndex);
    }

    /**
     * The rows not yet kept that sit below a changed row, nearest the change above
     * them first; rows as near as each other come in table order.
     *
     * CASES, per row not yet kept
     *   - a changed row stands above it -> a candidate, `rowIndex - that change's index` below it
     *   - no changed row stands above it -> never drawn: it would push a change down
     *
     * EXAMPLE, changed row indexes 2 and 7
     *   row 1 has no change above it                    -> left out
     *   row 3 is 3 - 2 = 1 below, row 8 is 8 - 7 = 1 below, row 4 is 4 - 2 = 2 below -> [3, 8, 4, …]
     */
    private static unchangedRowsBelowAChangeNearestFirst(rowReadings: TableRowReading[], keptRowIndexes: ReadonlySet<number>, changedRowIndexes: number[]): number[] {
        const rowsBelowNearestChangeAbove = (rowIndex: number): number | null => {
            const changesAbove = changedRowIndexes.filter((changedRowIndex) => changedRowIndex < rowIndex);
            /** NULL: no changed row stands above this row. */
            if (changesAbove.length === 0) return null;
            return rowIndex - Math.max(...changesAbove);
        };
        return rowReadings
            .flatMap((rowReading): UnchangedRowBelowAChange[] => {
                /** WHEN the row is kept already (a header or a changed row) CONSEQUENCE it is no candidate */
                if (keptRowIndexes.has(rowReading.rowIndex)) return [];
                const rowsBelowChange = rowsBelowNearestChangeAbove(rowReading.rowIndex);
                /** WHEN no changed row stands above it CONSEQUENCE it is never drawn */
                if (rowsBelowChange === null) return [];
                return [{ rowIndex: rowReading.rowIndex, rowsBelowChange }];
            })
            .sort((earlier, later) => earlier.rowsBelowChange - later.rowsBelowChange || earlier.rowIndex - later.rowIndex)
            .map((candidate) => candidate.rowIndex);
    }

    /**
     * Stands one gap row where the run of rows left out began, and takes the run's rows out.
     *
     * CASES
     *   - the run is empty  -> nothing: two kept rows sit side by side
     *   - otherwise         -> a gap counting the run's rows, before its first row
     */
    private static replaceRunWithGap(document: Document, rowsLeftOut: TableRowReading[], columnCount: number): void {
        const firstRowLeftOut = rowsLeftOut[0];
        /** WHEN the run is empty CONSEQUENCE no rows were left out here, so no gap is drawn. */
        if (firstRowLeftOut === undefined) return;
        const gapRow = document.createElementNS(CHANGE_EXCERPT_NAMESPACE, TABLE_ROWS_EXCERPT_GAP);
        gapRow.setAttributeNS(CHANGE_EXCERPT_NAMESPACE, TABLE_ROWS_EXCERPT_GAP_COUNT, String(rowsLeftOut.length));
        gapRow.setAttributeNS(CHANGE_EXCERPT_NAMESPACE, TABLE_ROWS_EXCERPT_GAP_COLUMNS, String(columnCount));
        firstRowLeftOut.row.parentNode?.insertBefore(gapRow, firstRowLeftOut.row);
        for (const rowLeftOut of rowsLeftOut) rowLeftOut.row.parentNode?.removeChild(rowLeftOut.row);
    }

    /** The table's own rows, in order; a nested table's rows belong to that table. */
    private static ownRowsOf(table: Element): Element[] {
        return Array.from(table.childNodes).filter((child): child is Element => child.nodeType === ELEMENT_NODE_TYPE && child.nodeName === "w:tr");
    }

    /** True when any paragraph of the row, a nested table's among them, holds one of the card's changes. */
    private static rowHoldsChange(row: Element, options: ExcerptChangeReading): boolean {
        return Array.from(row.getElementsByTagName("w:p")).some((paragraph) => ChangeExcerpt.paragraphHoldsChange(paragraph, options));
    }

    /** True when the row's properties mark it as a header row Word repeats (`w:trPr/w:tblHeader`). */
    private static isMarkedHeaderRow(row: Element): boolean {
        const rowProperties = Array.from(row.childNodes).find((child): child is Element => child.nodeType === ELEMENT_NODE_TYPE && child.nodeName === "w:trPr");
        /** WHEN the row has no properties CONSEQUENCE nothing marks it as a header. */
        if (rowProperties === undefined) return false;
        return rowProperties.getElementsByTagName("w:tblHeader").length > 0;
    }

    /**
     * How many grid columns the table has, from its `w:tblGrid`.
     *
     * CASES
     *   - the grid lists its columns   -> their count, the span a gap row needs
     *   - no grid, or an empty one     -> 1: the gap row still draws, one cell wide
     */
    private static gridColumnCountOf(table: Element): number {
        const gridColumns = Array.from(table.childNodes)
            .filter((child): child is Element => child.nodeType === ELEMENT_NODE_TYPE && child.nodeName === "w:tblGrid")
            .flatMap((grid) => Array.from(grid.getElementsByTagName("w:gridCol")));
        return Math.max(1, gridColumns.length);
    }

    /** True when a table encloses `element` below `root`: the outer table cuts its rows, and the nested one is drawn inside a kept cell. */
    private static standsInTable(element: Element, root: Element): boolean {
        for (let ancestor = element.parentNode; ancestor !== null && ancestor !== root; ancestor = ancestor.parentNode) {
            if (ancestor.nodeName === "w:tbl") return true;
        }
        return false;
    }
}
